import type { Rng } from '../../../core/rng';
import type { Lot, RoofMaterial } from '../types';
import { lotKey } from '../lotKey';
import committed from './roofReadings.json';
import type { BlockKeys } from './blockSides';
import type { PaintedRoof, PaintedRoofsFile } from './survey';

function fail(message: string): never {
  throw new Error(`roofReadings.json: ${message}`);
}

function isShare(v: unknown): v is number {
  return typeof v === 'number' && v >= 0 && v <= 1;
}

function parseRoof(key: string, value: unknown): PaintedRoof {
  const r = value as Partial<PaintedRoof> | null;
  const ok =
    r !== null &&
    typeof r === 'object' &&
    isShare(r.tile) &&
    isShare(r.reading) &&
    typeof r.spread === 'number' &&
    r.spread >= 0 &&
    Number.isInteger(r.pixels) &&
    r.pixels! > 0;
  if (!ok) fail(`the reading of ${key} is not a tile share, reading, spread and pixel count`);
  return { tile: r.tile, reading: r.reading, spread: r.spread, pixels: r.pixels! };
}

function parseSeen(value: unknown): Record<string, number> {
  if (value === null || typeof value !== 'object') fail('seen is not a map of pixel counts');
  const entries = Object.entries(value as Record<string, unknown>);
  const bad = entries.find(([, n]) => !Number.isInteger(n) || (n as number) <= 0);
  if (bad) fail(`seen gives ${bad[0]} no positive whole pixel count`);
  return Object.fromEntries(entries) as Record<string, number>;
}

function parseRoofs(name: string, value: unknown): Record<string, PaintedRoof> {
  if (value === null || typeof value !== 'object') fail(`${name} is not a map of readings`);
  return Object.fromEntries(
    Object.entries(value).map(([key, roof]) => [key, parseRoof(key, roof)])
  );
}

/** The survey's file, checked: tools/houses writes it (node tools/houses/run.mjs) and the build bundles it. */
export function parsePaintedRoofs(value: unknown): PaintedRoofsFile {
  const f = value as Partial<PaintedRoofsFile> | null;
  if (f === null || typeof f !== 'object' || typeof f.method !== 'string') fail('no method');
  const between = f.between;
  if (
    !between ||
    ![between.main, between.other, between.lastage].every((v) => typeof v === 'number' && v >= 0)
  )
    fail('no between-block spread for every setting');
  return {
    method: f.method,
    between,
    blocks: parseRoofs('blocks', f.blocks),
    sides: parseRoofs('sides', f.sides),
    seen: parseSeen(f.seen),
  };
}

export const PAINTED_ROOFS: PaintedRoofsFile = parsePaintedRoofs(committed);

/* The reading a lot's roof follows: its block's, else its street side's over the blocks not read on their own. */
function readingOf(k: BlockKeys, file: PaintedRoofsFile): string | null {
  if (file.blocks[k.block]) return k.block;
  return file.sides[k.side] ? k.side : null;
}

function groupsOf(keys: readonly BlockKeys[], file: PaintedRoofsFile): Map<string, number[]> {
  const groups = new Map<string, number[]>();
  keys.forEach((k, i) => {
    const key = readingOf(k, file);
    if (!key) return;
    const members = groups.get(key);
    if (members) members.push(i);
    else groups.set(key, [i]);
  });
  return groups;
}

/*
 * Lots in the order of their own draws, each flipped between tile and slate while that brings the
 * tiled weight nearer the share of the whole, until no flip does: a random choice of roofs that
 * shows the share as nearly as their sizes allow.
 */
function allocate(
  order: readonly number[],
  weight: (i: number) => number,
  share: number
): Set<number> {
  const target = share * order.reduce((sum, i) => sum + weight(i), 0);
  const tiled = new Set<number>();
  let sum = 0;
  let flipped = true;
  while (flipped) {
    flipped = false;
    for (const i of order) {
      const next = tiled.has(i) ? sum - weight(i) : sum + weight(i);
      if (Math.abs(next - target) >= Math.abs(sum - target)) continue;
      if (!tiled.delete(i)) tiled.add(i);
      sum = next;
      flipped = true;
    }
  }
  return tiled;
}

/*
 * A group's tiled lots: those the painter view shows by their seen roof pixels, so the view reads
 * the painted share, and the hidden ones by their area, so the block has it too.
 */
function tiledOf(
  lots: readonly Lot[],
  members: readonly number[],
  share: number,
  rng: Rng,
  seen: Readonly<Record<string, number>>
): Set<number> {
  const rank = new Map(
    members.map((i) => [i, rng.fork(lotKey(lots[i]!)).fork('paintedRoof').next()])
  );
  const order = [...members].sort((a, b) => rank.get(a)! - rank.get(b)!);
  const pixels = (i: number): number => seen[lotKey(lots[i]!)] ?? 0;
  const area = (i: number): number => lots[i]!.frontage * lots[i]!.depth;
  const shown = allocate(
    order.filter((i) => pixels(i) > 0),
    pixels,
    share
  );
  const hidden = allocate(
    order.filter((i) => pixels(i) === 0),
    area,
    share
  );
  return new Set([...shown, ...hidden]);
}

/**
 * Each lot's roof where the painting shows its block side, or its street side where no block of it
 * is legible: tile or slate, so the group's seen roofs and its hidden ones each show its painted
 * share. Null where neither is legible, and the lot draws from its district's shares.
 */
export function paintedRoofMaterials(
  lots: readonly Lot[],
  keys: readonly BlockKeys[],
  rng: Rng,
  file: PaintedRoofsFile = PAINTED_ROOFS
): (RoofMaterial | null)[] {
  const roofs = new Map<number, RoofMaterial>();
  for (const [key, members] of groupsOf(keys, file)) {
    const share = (file.blocks[key] ?? file.sides[key])!.tile;
    const tiled = tiledOf(lots, members, share, rng, file.seen);
    for (const i of members) roofs.set(i, tiled.has(i) ? 'tile' : 'slate');
  }
  return lots.map((_, i) => roofs.get(i) ?? null);
}
